import { useMemo } from "react";
import { type LookalikeAudienceStatisticsFileStructure } from "features/mediaDataRoom/models";
import { type MediaDataRoomJobHookResult } from "../useMediaDataRoomJob/models";
import { type LookalikeAudienceStatisticsHookResult } from "./models";
import {
  calculateAudienceQualityScore,
  roundExcludedAudienceSize,
} from "./utils";

interface LookalikeAudienceStatisticsStateHookPayload
  extends MediaDataRoomJobHookResult<LookalikeAudienceStatisticsFileStructure> {
  audienceId: string | undefined;
  excludeSeedAudience: boolean | undefined;
  audienceReach: number;
}

const useLookalikeAudienceStatisticsState = ({
  audienceId,
  audienceReach,
  excludeSeedAudience,
  computeResults,
  ...jobState
}: LookalikeAudienceStatisticsStateHookPayload): LookalikeAudienceStatisticsHookResult => {
  const {
    estimatedAudienceSize,
    addressableAudienceSize,
    seedAudienceSize,
    excludedAudienceSize,
    testSetPositiveExamplesSize,
    testSetSize,
    recall,
  } = useMemo(() => {
    if (!audienceId || !computeResults) {
      return {
        addressableAudienceSize: null,
        audienceQuality: null,
        estimatedAudienceSize: null,
        excludedAudienceSize: null,
        qualityScore: null,
        recall: null,
        seedAudienceSize: null,
        testSetPositiveExamplesSize: null,
        testSetSize: null,
      };
    }
    // Estimated Audience Size
    const { size: estimatedAudienceSize = 0, quality } =
      (computeResults.filtered_audiences || [])?.find(
        (audience) => audience.reach === audienceReach
      ) || {};
    // Detailed explanation of this calculation
    // https://www.notion.so/decentriq/MDCR-Update-LAL-estimated-audience-size-based-on-exclude-seed-audience-flag-144a4cd7fe55805e91a0c7a55eb3d925?pvs=4#144a4cd7fe5580438535fdd75e8d29bc
    const excludedAudienceSize =
      excludeSeedAudience &&
      quality?.roc_curve.tpr &&
      computeResults?.seed_audience_size
        ? roundExcludedAudienceSize(
            quality.roc_curve.tpr * computeResults.seed_audience_size
          )
        : null;
    return {
      addressableAudienceSize: computeResults?.addressable_audience_size || 0,
      estimatedAudienceSize,
      excludedAudienceSize,
      recall: quality?.roc_curve ?? null,
      seedAudienceSize: computeResults?.seed_audience_size || 0,
      testSetPositiveExamplesSize:
        computeResults?.quality?.roc_curve?.test_set_positive_examples_size ||
        0,
      testSetSize: computeResults?.quality?.roc_curve?.test_set_size || 0,
    };
  }, [audienceId, computeResults, excludeSeedAudience, audienceReach]);
  // Quality Score
  const audienceQualityScore = useMemo<number>(
    () =>
      calculateAudienceQualityScore(
        computeResults?.filtered_audiences || [],
        audienceReach
      ),
    [computeResults?.filtered_audiences, audienceReach]
  );
  return {
    addressableAudienceSize,
    audienceQualityScore,
    computeState: jobState,
    estimatedAudienceSize,
    excludedAudienceSize,
    rawStatistics: computeResults || null,
    recall,
    seedAudienceSize,
    testSetPositiveExamplesSize,
    testSetSize,
  };
};

export default useLookalikeAudienceStatisticsState;
